/**
 * PeriodLockingTab Component
 * 
 * This component provides the interface for accounting period locking functionality.
 * It prevents unauthorized modifications to closed accounting periods.
 * 
 * Features:
 * - Period locking to prevent modifications
 * - Balance validation before locking
 * - Complete audit trail of lock/unlock activities
 * - Password-protected operations with role-based access
 * - Full internationalization support (Arabic/English)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  LockClosedIcon, 
  CheckCircleIcon, 
  ClipboardDocumentIcon, 
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';
import PeriodLockingModal from '../PeriodLockingModal';

const PeriodLockingTab = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  const handleSuccess = () => {
    setShowModal(false);
    // Optionally refresh data
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t('period_locking.title', 'Period Locking')}
          </h2>
          <p className="text-gray-600 mt-1">
            {t('accounting.description')}
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2"
        >
          <LockClosedIcon className="h-4 w-4" />
          <span>{t('period_locking.manage_periods', 'Manage Periods')}</span>
        </button>
      </div>

      {/* Feature Description */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-red-900 mb-3">
          {t('payment_analytics.features')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <LockClosedIcon className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <h4 className="font-medium text-red-900 mb-1">{t('period_locking.lock_period', 'Lock Period')}</h4>
              <p className="text-red-700 text-sm">{t('period_locking.lock_period_desc', 'Prevent modifications to closed accounting periods')}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <CheckCircleIcon className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <h4 className="font-medium text-red-900 mb-1">{t('period_locking.validation', 'Validation')}</h4>
              <p className="text-red-700 text-sm">{t('period_locking.validation_desc', 'Verify period balance before locking')}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <ClipboardDocumentIcon className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <h4 className="font-medium text-red-900 mb-1">{t('period_locking.audit_trail', 'Audit Trail')}</h4>
              <p className="text-red-700 text-sm">{t('period_locking.audit_trail_desc', 'Complete audit trail of lock/unlock activities')}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <h4 className="font-medium text-red-900 mb-1">{t('settings.security')}</h4>
              <p className="text-red-700 text-sm">{t('period_locking.security_desc', 'Password-protected operations with role-based access')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-900">{t('common.warning')}</h4>
            <p className="text-amber-800 text-sm mt-1">
              {t('period_locking.warning_text', 'Locking a period prevents all modifications to journal entries within that period. This action should only be performed after thorough review and approval.')}
            </p>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <PeriodLockingModal
          onClose={() => setShowModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default PeriodLockingTab;
